import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Copy, Download, RotateCcw, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import LLMStream from "@/components/llm_response";

const ColorResults = ({ results, onReset }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [copiedColors, setCopiedColors] = useState(new Set());

  const copyToClipboard = async (color, label) => {
    try {
      await navigator.clipboard.writeText(color);
      setCopiedColors(prev => new Set([...prev, color]));
      toast({
        title: "Copied!",
        description: `${label} color ${color} copied to clipboard.`,
      });
      
      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedColors(prev => {
          const newSet = new Set(prev);
          newSet.delete(color);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      toast({
        title: "Copy Failed",
        description: "Unable to copy to clipboard.",
        variant: "destructive"
      });
    }
  };

  const downloadPalette = () => {
    const paletteData = {
      skinTone: results.skin_tone,
      eyeColor: results.eye_color,
      lipColor: results.lip_color,
      hairColor: results.hair_color,
      generatedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(paletteData, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'color-palette.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Downloaded!",
      description: "Your color palette has been downloaded.",
    });
  };

  const ColorCard = ({ label, color, icon, description }) => (
    <Card className="transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="text-2xl">{icon}</div>
          <div>
            <CardTitle className="text-lg">{label}</CardTitle>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div 
              className="w-16 h-16 rounded-lg border-2 border-gray-200 shadow-inner"
              style={{ backgroundColor: color }}
            />
            <div className="flex-1">
              <div className="font-mono text-lg font-semibold">{color}</div>
              <Button
                onClick={() => copyToClipboard(color, label)}
                variant="outline"
                size="sm"
                className="mt-2"
              >
                {copiedColors.has(color) ? (
                  <Check className="h-4 w-4 mr-1" />
                ) : (
                  <Copy className="h-4 w-4 mr-1" />
                )}
                {copiedColors.has(color) ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )

  const AISuggestionSection = () => {
    const [streamStarted, setStreamStarted] = useState(false);
    
    return (
    <div className="flex justify-center items-center">
      <div className="w-full">
        <Card className="max-w-4xl mx-auto m-4">
          <CardHeader>
            <CardTitle className="text-xl text-center">AI makeup suggestion</CardTitle>
          </CardHeader>
          <CardContent>
              <LLMStream
                face_params={[
                  results.skin_tone || "",
                  results.eye_color || "",
                  results.lip_color || "",
                  results.hair_color || ""
                ]}
                setStreamStarted={setStreamStarted}
                streamStarted={streamStarted}
              />
          </CardContent>
        </Card>
      </div>
    </div>
  )};
      
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={() => navigate("/")}
            variant="outline"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Button>
          <div className="flex items-center gap-4 ml-2 lg:ml-0">
            <Button
              onClick={onReset}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Capture Again
            </Button>
            <Button
              onClick={downloadPalette}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Download Palette</span>
            </Button>
          </div>
        </div>

        {/* Results Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mb-4">
            <Check className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
            Your Color Palette
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Here are your personalized HEX color codes extracted from your facial features using AI-powered analysis. 
            Click any color to copy it to your clipboard.
          </p>
        </div>

        {/* Color Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <ColorCard
            label="Skin Tone"
            color={results.skin_tone}
            icon="🏽"
            description="Dominant skin color from facial analysis"
          />
          <ColorCard
            label="Eye Color"
            color={results.eye_color}
            icon="👁️"
            description="Dominant iris color"
          />
          <ColorCard
            label="Lip Color"
            color={results.lip_color}
            icon="💋"
            description="Natural lip color"
          />
          <ColorCard
            label="Hair Color"
            color={results.hair_color}
            icon="💇"
            description="Dominant hair color"
          />
        </div>

        {/* Palette Summary */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle className="text-xl text-center">Complete Color Palette</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap justify-center gap-4 mb-6">
              {[
                { color: results.skin_tone, label: "Skin" },
                { color: results.eye_color, label: "Eyes" },
                { color: results.lip_color, label: "Lips" },
                { color: results.hair_color, label: "Hair" }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div 
                    className="w-20 h-20 rounded-full border-4 border-white shadow-lg mx-auto mb-2"
                    style={{ backgroundColor: item.color }}
                  />
                  <Badge variant="outline" className="text-xs">
                    {item.label}
                  </Badge>
                </div>
              ))}
            </div>
            
            <Separator className="my-6" />
            
            <div className="text-center space-y-4">
              <p className="text-gray-600">
                Your unique color palette has been generated using advanced facial recognition 
                with MediaPipe and K-means clustering algorithms. These colors represent your natural features 
                and can be used for makeup matching, fashion coordination, or design inspiration.
              </p>
              
              <div className="flex justify-center gap-4 flex-wrap">
                <Badge variant="secondary">AI-Powered Analysis</Badge>
                <Badge variant="secondary">MediaPipe + K-means</Badge>
                <Badge variant="secondary">Precise HEX Colors</Badge>
                <Badge variant="secondary">Privacy Protected</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AI makeup suggestion */}
        <AISuggestionSection/>
        
      </div>
    </div>
  );
};

export default ColorResults;